import React, { createContext, useContext, ReactNode } from 'react';
import { IAuthenticationPort } from '../ports/AuthenticationPort';
import { getAuthConfig, CloudProvider, ProviderType } from '../config/authConfig';
import { createAuthAdapter } from './createAuthAdapter';

export type { CloudProvider, ProviderType };

export class AuthenticationFactory {
  static async create(): Promise<IAuthenticationPort> {
    return createAuthAdapter();
  }
  
  static async createForCloud(
    cloud: CloudProvider,
    overrides?: {
      providerType?: ProviderType;
    }
  ): Promise<IAuthenticationPort> {
    return createAuthAdapter({
      cloudProvider: cloud,
      providerType: overrides?.providerType
    });
  }

  static getCurrentConfig() {
    return getAuthConfig();
  }
  
  static reset(): void {
    sessionStorage.removeItem('authPortInitialized');
    localStorage.removeItem('selectedLoginOption');
    localStorage.removeItem('providerType');
  }
  
  static getRegisteredAdapters(): string[] {
    return ['azure', 'gcp'];
  }
}

const AuthenticationPortContext = createContext<IAuthenticationPort | null>(null);

export interface AuthenticationPortProviderProps {
  children: ReactNode;
  authPort: IAuthenticationPort;
}

export const AuthenticationPortProvider: React.FC<AuthenticationPortProviderProps> = ({
  children,
  authPort
}) => {
  return (
    <AuthenticationPortContext.Provider value={authPort}>
      {children}
    </AuthenticationPortContext.Provider>
  );
};

export function useAuthenticationPort(): IAuthenticationPort {
  const context = useContext(AuthenticationPortContext);
  
  if (!context) {
    throw new Error(
      'useAuthenticationPort must be used within AuthenticationPortProvider. ' +
      'Wrap your component tree with <AuthenticationPortProvider>.'
    );
  }
  
  return context;
}